import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Container,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useParams } from 'react-router-dom';
import { getInternalProblem, languageOptions } from '../data/internalProblems';
import CodeEditor from './CodeEditor';
import { runJudge0Problem } from '../utils/judge0Runner';

function formatSeconds(seconds) {
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

function TestCaseBlock({ testCase, index, result }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: result?.passed ? 'rgba(21,144,95,0.35)' : 'divider',
        borderRadius: 2,
        bgcolor: result ? 'background.paper' : 'action.hover',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography sx={{ fontWeight: 800 }}>{testCase.name || `Case ${index + 1}`}</Typography>
        {result && (
          <Chip
            size="small"
            icon={result.passed ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
            label={result.passed ? 'Passed' : 'Failed'}
            sx={{
              color: result.passed ? 'success.main' : 'error.main',
              bgcolor: result.passed ? 'rgba(21,144,95,0.10)' : 'rgba(220,38,38,0.10)',
              border: '1px solid',
              borderColor: result.passed ? 'rgba(21,144,95,0.22)' : 'rgba(220,38,38,0.22)',
              '& .MuiChip-icon': {
                color: result.passed ? 'success.main' : 'error.main',
              },
            }}
          />
        )}
      </Stack>
      <Typography sx={{ color: 'text.secondary', mt: 1, fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
        input: {JSON.stringify(testCase.input)}
      </Typography>
      <Typography sx={{ color: 'text.secondary', mt: 0.5, fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
        expected: {JSON.stringify(testCase.expected)}
      </Typography>
      {testCase.explanation && (
        <Typography sx={{ color: 'text.secondary', mt: 1, fontSize: '0.9rem', lineHeight: 1.55 }}>
          {testCase.explanation}
        </Typography>
      )}
      {result && (
        <Typography sx={{ color: result.passed ? 'success.main' : 'error.main', mt: 0.5, fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
          actual: {JSON.stringify(result.actual)}
        </Typography>
      )}
    </Paper>
  );
}

function buildLearnPlan(problem) {
  const firstCase = problem.testCases?.[0];
  const category = problem.category || 'Practice';
  const signals = {
    'Binary Search': ['Sorted or answer-space search', 'A monotonic condition', 'Need the first/last feasible value'],
    'Arrays & Hashing': ['Fast lookup needed', 'Counting or grouping', 'Need to remember seen values'],
    'Two Pointers': ['Pairs in sorted data', 'Converging choices', 'Linear scan with two indexes'],
    'Sliding Window': ['Contiguous subarray or substring', 'Maintain a valid window', 'Expand right and repair left'],
    Trees: ['Recursive structure', 'Need subtree answers', 'Traversal order matters'],
    Graphs: ['Nodes and edges', 'Reachability or components', 'Visited state is required'],
    'Dynamic Programming': ['Overlapping subproblems', 'Choice at each state', 'Need optimal count or possibility'],
    Stack: ['Nested structure', 'Undo/recent item matters', 'Resolve when a condition breaks'],
  };

  const defaultSignals = ['Identify the input shape', 'Choose the smallest invariant', 'Protect edge cases before optimizing'];
  const recognition = signals[category] || defaultSignals;

  return {
    intuition: `Treat this as a ${category} problem. Before coding, name the invariant your solution must preserve and decide what state changes after each step.`,
    recognition,
    steps: [
      'Restate the goal in one sentence.',
      'Choose the data structure or pointer state that preserves the invariant.',
      'Trace the first example manually and write down each state change.',
      'Code the template, then test empty, single-item, duplicate, and boundary inputs.',
    ],
    pitfalls: [
      'Starting from code before the invariant is clear.',
      'Only testing the happy path.',
      'Forgetting that inputs can hit the smallest or largest allowed size.',
    ],
    dryRun: firstCase
      ? `Start with input ${JSON.stringify(firstCase.input)}. The expected output is ${JSON.stringify(firstCase.expected)}; every step should move state closer to that result without breaking the invariant.`
      : 'Pick the smallest sample input and trace it one state transition at a time.',
  };
}

function compactJson(value, maxLength = 90) {
  const text = JSON.stringify(value);
  if (!text) return 'null';
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}...` : text;
}

function normalizeProblemCategory(problem) {
  const raw = problem.category || '';
  const map = {
    ArraysAndHashing: 'Arrays & Hashing',
    BinarySearch: 'Binary Search',
    TwoPointer: 'Two Pointers',
    SlidingWindow: 'Sliding Window',
    LinkedList: 'Linked List',
    DynamicProgramming: 'Dynamic Programming',
    MonotonicStack: 'Monotonic Stack',
    DivideAndConquer: 'Divide & Conquer',
    PrefixSum: 'Prefix Sum',
    SortedList: 'Sorted List',
    SegmentTree: 'Segment Tree',
    Parenthesis: 'Parentheses',
    Graph: 'Graphs',
    Tree: 'Trees',
  };
  if (map[raw]) return map[raw];
  if (raw && raw !== 'Practice' && raw !== 'Course Practice') return raw;

  const id = problem.id || '';
  const title = problem.title || '';
  const text = `${id} ${title}`.toLowerCase();
  if (text.includes('sqrt') || text.includes('search') || text.includes('koko')) return 'Binary Search';
  if (text.includes('window') || text.includes('substring')) return 'Sliding Window';
  if (text.includes('palindrome') || text.includes('two-sum-ii') || text.includes('3sum')) return 'Two Pointers';
  if (text.includes('parentheses') || text.includes('bracket')) return 'Parentheses';
  if (text.includes('tree') || text.includes('bst')) return 'Trees';
  if (text.includes('island') || text.includes('graph') || text.includes('course')) return 'Graphs';
  if (text.includes('heap') || text.includes('kth') || text.includes('median')) return 'Heap';
  if (text.includes('interval') || text.includes('meeting')) return 'Interval';
  if (text.includes('prefix') || text.includes('subarray')) return 'Prefix Sum';
  if (text.includes('climb') || text.includes('robber') || text.includes('coin')) return 'Dynamic Programming';
  if (text.includes('stack') || text.includes('temperature')) return 'Monotonic Stack';
  return 'Practice';
}

function findTraceSubject(problem, input) {
  const args = problem.signature?.args || [];
  const entries = Object.entries(input || {});
  const byType = (type) => {
    const arg = args.find((item) => item.type === type && input?.[item.name] !== undefined);
    return arg ? { name: arg.name, value: input[arg.name], type } : null;
  };
  const fallbackArray = entries.find(([, value]) => Array.isArray(value) && !Array.isArray(value[0]));
  const fallbackMatrix = entries.find(([, value]) => Array.isArray(value) && Array.isArray(value[0]));
  const fallbackString = entries.find(([, value]) => typeof value === 'string');
  const fallbackInt = entries.find(([, value]) => typeof value === 'number');

  return {
    array: byType('intArray') || byType('stringArray') || (fallbackArray && { name: fallbackArray[0], value: fallbackArray[1], type: 'array' }),
    matrix: byType('intMatrix') || (fallbackMatrix && { name: fallbackMatrix[0], value: fallbackMatrix[1], type: 'matrix' }),
    string: byType('string') || (fallbackString && { name: fallbackString[0], value: fallbackString[1], type: 'string' }),
    int: byType('int') || (fallbackInt && { name: fallbackInt[0], value: fallbackInt[1], type: 'int' }),
    entries,
  };
}

function buildProblemTrace(problem, testCase) {
  const input = testCase?.input ?? {};
  const expected = testCase?.expected ?? null;
  const category = normalizeProblemCategory(problem);
  const subject = findTraceSubject(problem, input);
  const base = [
    {
      label: 'Input',
      value: compactJson(input),
      note: 'Read this exact test case and decide which part of the state changes on each move.',
    },
  ];

  const templates = {
    'Binary Search': [
      ['Range', 'Choose the left and right bounds that still contain the answer.'],
      ['Probe', 'Test the middle value or candidate speed.'],
      ['Discard', 'Use the monotonic condition to throw away one side.'],
    ],
    'Arrays & Hashing': [
      ['Scan', 'Process one item at a time.'],
      ['Lookup', 'Ask what value, count, or key would finish the current decision.'],
      ['Store', 'Save only the information future steps need.'],
    ],
    'Two Pointers': [
      ['Left / right', 'Place pointers at the useful extremes or scan positions.'],
      ['Compare', 'Use the current pair to decide which pointer can move.'],
      ['Converge', 'Move without losing any possible answer.'],
    ],
    'Sliding Window': [
      ['Expand', 'Move the right edge to include new information.'],
      ['Repair', 'Move the left edge until the window is valid again.'],
      ['Record', 'Update the best/count once the invariant holds.'],
    ],
    'Linked List': [
      ['Current node', 'Keep a pointer to the node being processed.'],
      ['Save next', 'Do not lose the rest of the list before rewiring.'],
      ['Rewire', 'Change one pointer, then advance.'],
    ],
    Trees: [
      ['Visit', 'Decide what this node contributes.'],
      ['Children', 'Ask left and right subtrees for their return values.'],
      ['Return', 'Return exactly the state the parent needs.'],
    ],
    Graphs: [
      ['Frontier', 'Start from a node or cell that still needs exploration.'],
      ['Visited', 'Mark state so each node is processed once.'],
      ['Neighbors', 'Push valid adjacent states and ignore invalid ones.'],
    ],
    'Dynamic Programming': [
      ['State', 'Define the smaller subproblem.'],
      ['Transition', 'Build this state from earlier states.'],
      ['Order', 'Compute states only after dependencies are ready.'],
    ],
    Heap: [
      ['Candidate', 'Push the next item with its priority.'],
      ['Trim', 'Pop when the heap contains items that should not survive.'],
      ['Top', 'Read the boundary item from the heap.'],
    ],
    Interval: [
      ['Sort', 'Sort intervals so overlaps become local.'],
      ['Compare', 'Compare the next interval against the active range.'],
      ['Merge / split', 'Either extend the active range or start a new one.'],
    ],
    'Monotonic Stack': [
      ['Read', 'Scan the next value.'],
      ['Pop', 'Resolve older values while the monotonic order breaks.'],
      ['Push', 'Store the current index as unresolved.'],
    ],
    Parentheses: [
      ['Read char', 'Classify the current character.'],
      ['Stack / balance', 'Match closers against the most recent opener or update balance.'],
      ['Validate', 'Finish only when nothing is unmatched.'],
    ],
    'Prefix Sum': [
      ['Prefix', 'Update the running total.'],
      ['Difference', 'Use prefix differences to represent a range.'],
      ['Count', 'Store prefix states for future ranges.'],
    ],
  };
  const patternSteps = templates[category] || [
    ['Invariant', 'Name the condition that stays true after every step.'],
    ['Transition', 'Change one piece of state while preserving the invariant.'],
    ['Check', 'Compare the final state against the expected result.'],
  ];

  return {
    title: `${category} trace`,
    category,
    input,
    expected,
    subject,
    steps: [
      ...base,
      ...patternSteps.map(([label, note]) => ({ label, value: label.toLowerCase(), note })),
      {
        label: 'Output',
        value: compactJson(expected),
        note: `This test case expects ${compactJson(expected)}. The trace should explain that exact result.`,
      },
    ],
  };
}

function TraceVisual({ trace, activeStep }) {
  const { subject, category } = trace;
  const activeLabel = trace.steps[activeStep]?.label || '';
  const arrayValues = subject.array?.value;
  const matrixValues = subject.matrix?.value;
  const stringValue = subject.string?.value;
  const maxCells = 14;
  const highlightIndex = Math.min(Math.max(activeStep - 1, 0), maxCells - 1);

  if (matrixValues?.length) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(matrixValues[0]?.length || 1, 6)}, minmax(0, 1fr))`, gap: 0.7 }}>
        {matrixValues.slice(0, 6).flatMap((row, rowIndex) => row.slice(0, 6).map((value, colIndex) => {
          const active = (rowIndex + colIndex) % Math.max(1, activeStep + 1) === 0 && activeStep > 0;
          return (
            <Box key={`${rowIndex}-${colIndex}`} sx={{ aspectRatio: '1 / 1', borderRadius: 1.3, display: 'grid', placeItems: 'center', bgcolor: active ? 'primary.main' : 'action.hover', color: active ? '#fff' : 'text.primary', border: '1px solid', borderColor: active ? 'primary.main' : 'divider', fontWeight: 850, fontSize: '0.78rem' }}>
              {String(value)}
            </Box>
          );
        }))}
      </Box>
    );
  }

  if (Array.isArray(arrayValues) && arrayValues.length) {
    const isInterval = Array.isArray(arrayValues[0]) || category === 'Interval';
    if (isInterval) {
      return (
        <Stack spacing={0.8}>
          {arrayValues.slice(0, 5).map((item, index) => (
            <Box key={`${compactJson(item)}-${index}`} sx={{ p: 1, borderRadius: 2, bgcolor: index === highlightIndex ? 'rgba(47,99,255,0.12)' : 'action.hover', border: '1px solid', borderColor: index === highlightIndex ? 'primary.main' : 'divider', display: 'grid', gridTemplateColumns: '42px 1fr', gap: 1, alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 900, color: 'text.secondary', fontSize: '0.75rem' }}>{index + 1}</Typography>
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 800 }}>{compactJson(item, 42)}</Typography>
            </Box>
          ))}
        </Stack>
      );
    }

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(arrayValues.length, maxCells)}, minmax(0, 1fr))`, gap: 0.55, alignItems: 'end' }}>
        {arrayValues.slice(0, maxCells).map((value, index) => {
          const active = index === highlightIndex || (category.includes('Two') && (index === 0 || index === Math.min(arrayValues.length, maxCells) - 1));
          return (
            <Box key={`${value}-${index}`} sx={{ minHeight: 46 + Math.min(60, Math.abs(Number(value) || index) * 4), borderRadius: '14px 14px 8px 8px', display: 'grid', placeItems: 'center', bgcolor: active ? 'primary.main' : 'rgba(47,99,255,0.1)', color: active ? '#fff' : 'primary.main', border: '1px solid', borderColor: active ? 'primary.main' : 'rgba(47,99,255,0.22)', fontWeight: 900, fontSize: '0.78rem' }}>
              {String(value)}
            </Box>
          );
        })}
      </Box>
    );
  }

  if (stringValue) {
    const chars = String(stringValue).slice(0, maxCells).split('');
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${chars.length}, minmax(0, 1fr))`, gap: 0.55 }}>
        {chars.map((char, index) => {
          const active = index === highlightIndex || (category === 'Parentheses' && index <= activeStep);
          return (
            <Box key={`${char}-${index}`} sx={{ aspectRatio: '1 / 1', minWidth: 0, borderRadius: 1.3, border: '1px solid', borderColor: active ? 'primary.main' : 'divider', bgcolor: active ? 'primary.main' : 'action.hover', color: active ? '#fff' : 'text.primary', display: 'grid', placeItems: 'center', fontWeight: 900 }}>
              {char}
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
      {['Input', activeLabel, 'State', 'Output'].map((item, index) => (
        <Box key={`${item}-${index}`} sx={{ minHeight: 84, p: 1.2, borderRadius: 2, bgcolor: index === Math.min(activeStep, 3) ? 'primary.main' : 'action.hover', color: index === Math.min(activeStep, 3) ? '#fff' : 'text.primary', border: '1px solid', borderColor: index === Math.min(activeStep, 3) ? 'primary.main' : 'divider' }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.84rem' }}>{item}</Typography>
          <Typography sx={{ mt: 0.6, opacity: 0.72, fontSize: '0.72rem', fontFamily: 'monospace' }}>
            {index === 0 ? compactJson(trace.input, 34) : index === 3 ? compactJson(trace.expected, 34) : trace.category}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function ProblemTracePanel({ problem }) {
  const [caseIndex, setCaseIndex] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const testCases = useMemo(() => problem.testCases || [], [problem.testCases]);
  const selectedCase = useMemo(
    () => testCases[caseIndex] || testCases[0] || { input: {}, expected: null },
    [caseIndex, testCases]
  );
  const trace = useMemo(() => buildProblemTrace(problem, selectedCase), [problem, selectedCase]);
  const step = trace.steps[activeStep];

  React.useEffect(() => {
    setActiveStep(0);
  }, [caseIndex, problem.id]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        boxShadow: 'none',
        position: 'relative',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 900 }}>Interactive trace</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem' }}>
            Case {caseIndex + 1} of {Math.max(testCases.length, 1)} · Step {activeStep + 1} of {trace.steps.length}
          </Typography>
        </Box>
        <Chip label={trace.title} size="small" sx={{ fontWeight: 800 }} />
      </Stack>

      {testCases.length > 1 && (
        <Stack direction="row" spacing={0.8} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 0.8 }}>
          {testCases.map((testCase, index) => (
            <Chip
              key={`${testCase.name || 'case'}-${index}`}
              label={testCase.name || `Case ${index + 1}`}
              onClick={() => setCaseIndex(index)}
              variant={index === caseIndex ? 'filled' : 'outlined'}
              color={index === caseIndex ? 'primary' : 'default'}
              sx={{ fontWeight: 800 }}
            />
          ))}
        </Stack>
      )}

      <Box sx={{ mb: 2 }}>
        <TraceVisual trace={trace} activeStep={activeStep} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(5, minmax(0, 1fr))' },
          gap: 1,
          mb: 2,
        }}
      >
        {trace.steps.map((item, index) => {
          const active = index === activeStep;
          const done = index < activeStep;
          return (
            <ButtonBase
              key={`${item.label}-${index}`}
              onClick={() => setActiveStep(index)}
              sx={{
                p: 1.2,
                minHeight: 92,
                alignItems: 'stretch',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                borderRadius: 2,
                border: '1px solid',
                borderColor: active ? 'primary.main' : done ? 'rgba(21,144,95,0.24)' : 'divider',
                bgcolor: active ? 'rgba(47,99,255,0.1)' : done ? 'rgba(21,144,95,0.08)' : 'action.hover',
                textAlign: 'left',
              }}
            >
              <Typography sx={{ width: '100%', fontSize: '0.7rem', color: 'text.secondary', fontWeight: 900 }}>
                {String(index + 1).padStart(2, '0')}
              </Typography>
              <Typography sx={{ width: '100%', fontWeight: 850, lineHeight: 1.2, mt: 0.5 }}>
                {item.label}
              </Typography>
              <Typography
                sx={{
                  width: '100%',
                  color: 'text.secondary',
                  fontSize: '0.72rem',
                  mt: 0.6,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontFamily: item.value?.startsWith?.('{') || item.value?.startsWith?.('[') ? 'monospace' : 'inherit',
                }}
              >
                {item.value}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>

      <Paper variant="outlined" sx={{ p: 1.6, bgcolor: 'action.hover', boxShadow: 'none' }}>
        <Typography sx={{ fontWeight: 850 }}>{step.label}</Typography>
        <Typography sx={{ color: 'text.secondary', lineHeight: 1.6, mt: 0.6 }}>{step.note}</Typography>
      </Paper>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button
          variant="outlined"
          onClick={() => setActiveStep((current) => Math.max(0, current - 1))}
          disabled={activeStep === 0}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => setActiveStep((current) => Math.min(trace.steps.length - 1, current + 1))}
          disabled={activeStep === trace.steps.length - 1}
        >
          Advance step
        </Button>
      </Stack>
    </Paper>
  );
}

function LearnPanel({ problem }) {
  const plan = useMemo(() => buildLearnPlan(problem), [problem]);

  return (
    <Stack spacing={2.2}>
      <Paper variant="outlined" sx={{ p: 2.5, bgcolor: 'action.hover', boxShadow: 'none' }}>
        <Typography sx={{ fontWeight: 850, mb: 0.75 }}>Intuition</Typography>
        <Typography sx={{ color: 'text.secondary', lineHeight: 1.65 }}>{plan.intuition}</Typography>
      </Paper>

      <ProblemTracePanel problem={problem} />

      <Box>
        <Typography sx={{ fontWeight: 850, mb: 1 }}>Recognition signals</Typography>
        <Stack spacing={1}>
          {plan.recognition.map((item) => (
            <Stack direction="row" spacing={1.3} alignItems="flex-start" key={item}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'secondary.main', mt: 1, flexShrink: 0 }} />
              <Typography sx={{ color: 'text.secondary', lineHeight: 1.55 }}>{item}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Box>
        <Typography sx={{ fontWeight: 850, mb: 1 }}>Solve plan</Typography>
        <Stack spacing={1}>
          {plan.steps.map((item, index) => (
            <Paper key={item} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper', boxShadow: 'none' }}>
              <Typography sx={{ color: 'text.primary', fontWeight: 750 }}>
                {index + 1}. {item}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </Box>

      <Paper variant="outlined" sx={{ p: 2.5, boxShadow: 'none' }}>
        <Typography sx={{ fontWeight: 850, mb: 0.75 }}>Example dry run</Typography>
        <Typography sx={{ color: 'text.secondary', lineHeight: 1.65 }}>{plan.dryRun}</Typography>
      </Paper>

      <Box>
        <Typography sx={{ fontWeight: 850, mb: 1 }}>Common mistakes</Typography>
        <Stack spacing={0.8}>
          {plan.pitfalls.map((item) => (
            <Typography key={item} sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
              - {item}
            </Typography>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

export default function ProblemWorkspace() {
  const { problemSlug } = useParams();
  const problem = useMemo(() => getInternalProblem(problemSlug), [problemSlug]);
  const [language, setLanguage] = useState(languageOptions[0]);
  const [code, setCode] = useState(problem.starterCode[languageOptions[0].key]);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerStartedAt, setTimerStartedAt] = useState(null);
  const [accumulatedSeconds, setAccumulatedSeconds] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [leftTab, setLeftTab] = useState('prompt');

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    setCode(problem.starterCode[language.key] || problem.starterCode.python);
    setResults([]);
    setError('');
    setIsTimerRunning(false);
    setTimerStartedAt(null);
    setAccumulatedSeconds(0);
    setElapsedSeconds(0);
    setLeftTab('prompt');
  }, [problemSlug, problem, language.key]);

  React.useEffect(() => {
    if (!isTimerRunning || timerStartedAt === null) return undefined;

    const timer = window.setInterval(() => {
      setElapsedSeconds(accumulatedSeconds + Math.floor((Date.now() - timerStartedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [accumulatedSeconds, isTimerRunning, timerStartedAt]);

  const handleLanguageChange = (event) => {
    const nextLanguage = languageOptions.find((item) => item.key === event.target.value);
    setLanguage(nextLanguage);
  };

  const startTimer = () => {
    if (isTimerRunning) return;
    setTimerStartedAt(Date.now());
    setIsTimerRunning(true);
  };

  const stopTimer = () => {
    if (!isTimerRunning || timerStartedAt === null) return;
    const nextElapsed = accumulatedSeconds + Math.floor((Date.now() - timerStartedAt) / 1000);
    setAccumulatedSeconds(nextElapsed);
    setElapsedSeconds(nextElapsed);
    setTimerStartedAt(null);
    setIsTimerRunning(false);
  };

  const resetTimer = () => {
    setIsTimerRunning(false);
    setTimerStartedAt(null);
    setAccumulatedSeconds(0);
    setElapsedSeconds(0);
  };

  const runTests = async () => {
    setRunning(true);
    setError('');
    setResults([]);

    try {
      const payload = await runJudge0Problem(problem, language, code);
      if (payload.error) setError(payload.error);
      const nextResults = payload.results || [];
      setResults(nextResults);
      const allPassed = nextResults.length > 0 && nextResults.every((result) => result.passed);
      const history = JSON.parse(localStorage.getItem('problemRunStats') || '{}');
      localStorage.setItem('problemRunStats', JSON.stringify({
        ...history,
        [problem.id]: {
          title: problem.title,
          category: problem.category,
          difficulty: problem.difficulty,
          language: language.key,
          passed: allPassed,
          passedCount: nextResults.filter((result) => result.passed).length,
          totalCount: nextResults.length,
          elapsedSeconds,
          updatedAt: new Date().toISOString(),
        },
      }));
    } catch (err) {
      setError(err.message || 'Code execution failed.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 3 } }}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3.5 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              bgcolor: 'background.paper',
              minHeight: { md: 'calc(100vh - 180px)' },
            }}
          >
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              <Chip label={problem.difficulty} size="small" />
              <Chip label={problem.category} size="small" variant="outlined" />
              <Chip label={language.label} size="small" variant="outlined" />
            </Stack>
            <Typography variant="h3" sx={{ fontWeight: 850, letterSpacing: 0, mb: 2 }}>
              {problem.title}
            </Typography>
            <Tabs value={leftTab} onChange={(_, value) => setLeftTab(value)} sx={{ mb: 2 }}>
              <Tab value="prompt" label="Prompt" />
              <Tab value="learn" label="Learn" />
            </Tabs>

            {leftTab === 'prompt' ? (
              <>
                <Typography sx={{ color: 'text.secondary', fontSize: '1.05rem', lineHeight: 1.7, mb: 3 }}>
                  {problem.prompt}
                </Typography>

                {problem.constraints?.length > 0 && (
                  <>
                    <Typography sx={{ fontWeight: 800, mb: 1 }}>Constraints</Typography>
                    <Box component="ul" sx={{ color: 'text.secondary', pl: 2.5, mt: 0, mb: 3, lineHeight: 1.7 }}>
                      {problem.constraints.map((constraint) => (
                        <li key={constraint}>{constraint}</li>
                      ))}
                    </Box>
                  </>
                )}

                {problem.testCases.length > 0 && (
                  <>
                    <Typography sx={{ fontWeight: 800, mb: 1 }}>Examples & tests</Typography>
                    <Stack spacing={1.2}>
                      {problem.testCases.map((testCase, index) => (
                        <TestCaseBlock key={`${testCase.name || 'Case'}-${index}`} testCase={testCase} index={index} />
                      ))}
                    </Stack>
                  </>
                )}
              </>
            ) : (
              <LearnPanel problem={problem} />
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              bgcolor: 'background.paper',
              minHeight: { md: 'calc(100vh - 180px)' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Typography sx={{ fontWeight: 850 }}>Editor</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={formatSeconds(elapsedSeconds)}
                  size="small"
                  sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 850 }}
                />
                <Select value={language.key} size="small" onChange={handleLanguageChange}>
                  {languageOptions.map((option) => (
                    <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
                  ))}
                </Select>
              </Stack>
            </Box>
            <CodeEditor
              value={code}
              onChange={setCode}
              languageKey={language.key}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={runTests}
                disabled={running}
                sx={{ borderRadius: 999, px: 3 }}
              >
                {running ? 'Running...' : 'Run tests'}
              </Button>
              <Button
                variant="outlined"
                startIcon={isTimerRunning ? <PauseIcon /> : <PlayArrowIcon />}
                onClick={isTimerRunning ? stopTimer : startTimer}
                sx={{ borderRadius: 999, px: 2.5 }}
              >
                {isTimerRunning ? 'Stop timer' : 'Start timer'}
              </Button>
              <Button
                variant="text"
                startIcon={<RestartAltIcon />}
                onClick={resetTimer}
                sx={{ borderRadius: 999, px: 2.5 }}
              >
                Reset
              </Button>
            </Stack>
          </Paper>

          {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}

          {results.length > 0 && (
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 850 }}>Run results</Typography>
              {results.map((result, index) => (
                <TestCaseBlock
                  key={`${result.name || 'Result'}-${index}`}
                  testCase={{
                    name: result.name,
                    input: result.input,
                    expected: result.expected,
                  }}
                  index={index}
                  result={result}
                />
              ))}
            </Stack>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}
